/*Copyright (c) 2026. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for any purpose without the express written permission of Honeywell. */

const LATITUDE_LIMIT = 90;
const LONGITUDE_LIMIT = 180;

const roundToFiveDecimals = (value: number): number => {
  return Math.round(value * 100000) / 100000;
};

const isInRange = (
  value: number,
  maxAbs: number
): boolean => {
  return !Number.isNaN(value) && Math.abs(value) <= maxAbs;
};

const parseDecimalInput = (
  value: string,
  maxAbs: number
): number | null => {
  const cleaned = value.replace(
    /[^0-9.-]/g,
    ""
  );

  if (
    !cleaned ||
    cleaned === "-" ||
    cleaned === "." ||
    cleaned === "-."
  ) {
    return null;
  }

  const parsed = Number(cleaned);

  if (!isInRange(parsed, maxAbs)) {
    return null;
  }

  return parsed;
};

const parseDmsInput = (
  value: string,
  maxAbs: number
): number | null => {
  const normalized = value
    .replace(/’/g, "'")
    .replace(/′/g, "'")
    .replace(/""/g, '"')
    .replace(/″/g, '"')
    .trim();

  const dmsRegex =
    /^([+-]?)(\d{1,3})\s*°\s*(\d{1,2})\s*'\s*(\d{1,2})(?:\.(\d{1,2}))?\s*(?:"|''?)?$/;

  const match = normalized.match(dmsRegex);

  if (!match) {
    return null;
  }

  const sign = match[1] === "-" ? -1 : 1;
  const degrees = Number(match[2]);
  const minutes = Number(match[3]);
  const seconds = Number(match[4]);
  const secondsDecimal = Number(match[5] || "0") / 100;
  const fullSeconds = seconds + secondsDecimal;

  if (
    Number.isNaN(degrees) ||
    Number.isNaN(minutes) ||
    Number.isNaN(seconds) ||
    minutes > 59 ||
    fullSeconds >= 60
  ) {
    return null;
  }

  const decimal = sign * (
    degrees + minutes / 60 + fullSeconds / 3600
  );

  if (!isInRange(decimal, maxAbs)) {
    return null;
  }

  return decimal;
};

const parseCoordinateToDecimal = (
  value: string,
  maxAbs: number
): number | null => {
  if (!value || !value.trim()) {
    return null;
  }

  const parsedDms = parseDmsInput(
    value,
    maxAbs
  );

  if (parsedDms !== null) {
    return parsedDms;
  }

  return parseDecimalInput(
    value,
    maxAbs
  );
};

const pad2 = (value: number): string => {
  return String(value).padStart(2, "0");
};

const formatDecimalToDms = (
  decimalValue: number
): string => {
  const sign = decimalValue < 0 ? "-" : "";
  let absolute = Math.abs(decimalValue);
  let degrees = Math.floor(absolute);
  absolute = (absolute - degrees) * 60;
  let minutes = Math.floor(absolute);
  let seconds = (absolute - minutes) * 60;

  seconds = Math.round(seconds * 100) / 100;

  if (seconds >= 60) {
    seconds = 0;
    minutes += 1;
  }

  if (minutes >= 60) {
    minutes = 0;
    degrees += 1;
  }

  const secondsInteger = Math.floor(seconds);
  const secondsDecimal = Math.round(
    (seconds - secondsInteger) * 100
  );

  return `${sign}${degrees}° ${pad2(minutes)}′ ${pad2(secondsInteger)}.${pad2(secondsDecimal)}″`;
};

const formatCoordinateForUI = (
  value: string,
  maxAbs: number
): string => {
  const decimal = parseCoordinateToDecimal(
    value,
    maxAbs
  );

  if (decimal === null) {
    return "";
  }

  return formatDecimalToDms(decimal);
};

const convertToApiDecimal = (
  value: string,
  maxAbs: number
): string => {
  const decimal = parseCoordinateToDecimal(
    value,
    maxAbs
  );

  if (decimal === null) {
    return "";
  }

  return roundToFiveDecimals(decimal).toFixed(5);
};

export const formatLatitudeForUI = (
  value: string
) => {
  return formatCoordinateForUI(
    value,
    LATITUDE_LIMIT
  );
};

export const formatLongitudeForUI = (
  value: string
) => {
  return formatCoordinateForUI(
    value,
    LONGITUDE_LIMIT
  );
};

export const latitudeToAPI = (
  value: string
) => {
  return convertToApiDecimal(
    value,
    LATITUDE_LIMIT
  );
};

export const longitudeToAPI = (
  value: string
) => {
  return convertToApiDecimal(
    value,
    LONGITUDE_LIMIT
  );
};

export const latitudeFromAPI = (
  value: string | number
) => {
  return formatCoordinateForUI(
    String(value),
    LATITUDE_LIMIT
  );
};

export const longitudeFromAPI = (
  value: string | number
) => {
  return formatCoordinateForUI(
    String(value),
    LONGITUDE_LIMIT
  );
};

export const validateLatitude = (
  value: string
): boolean => {
  return (
    parseCoordinateToDecimal(
      value,
      LATITUDE_LIMIT
    ) !== null
  );
};

export const validateLongitude = (
  value: string
): boolean => {
  return (
    parseCoordinateToDecimal(
      value,
      LONGITUDE_LIMIT
    ) !== null
  );
};
